import { supabase } from './supabase';
import { allowedSetNumbers, getSessionSetLimit } from '../data/examCatalog';

export interface BankQuestion {
  id: number;
  setNo: number;
  sourceNo: number | null;
  category: string;
  q: string;
  opts: string[];
  /** 0-based indexes of the correct options; empty when the answer key is unknown. */
  ans: number[];
  explain: string;
}

export interface BankData {
  /** Every set of the exam, with whether the learner may open it (user_access.no_of_sets). */
  sets: { set: number; unlocked: boolean }[];
  /** Questions of the unlocked sets only: the other sets are never requested. */
  questions: BankQuestion[];
}

interface Row {
  id: number; set_no: number; source_no: number | null; category: string | null; question: string;
  option1: string | null; option2: string | null; option3: string | null; option4: string | null; option5: string | null; option6: string | null;
  total_option: number | null; correct_ans: string | null; explanation: string | null;
}

const PAGE = 1000; // Supabase returns at most 1000 rows per request

const toQuestion = (r: Row): BankQuestion => {
  const all = [r.option1, r.option2, r.option3, r.option4, r.option5, r.option6];
  const total = r.total_option && r.total_option > 0 ? Math.min(r.total_option, 6) : all.filter(o => o).length;
  const opts = all.slice(0, total).map(o => o ?? '');
  const ans = String(r.correct_ans ?? '').split(',').map(x => parseInt(x.trim(), 10) - 1).filter(n => !isNaN(n) && n >= 0 && n < opts.length);
  return { id: r.id, setNo: r.set_no, sourceNo: r.source_no, category: r.category || 'General', q: r.question, opts, ans, explain: r.explanation || '' };
};

export async function fetchQuestionBank(examId: number): Promise<{ success: boolean; data?: BankData; error?: string }> {
  try {
    // 1. which sets exist
    const setRes = await supabase.from('question_bank').select('set_no').eq('exam_id', examId).limit(5000);
    if (setRes.error) return { success: false, error: setRes.error.message };
    const all = Array.from(new Set((setRes.data || []).map(r => r.set_no as number))).sort((a, b) => a - b);
    const open = allowedSetNumbers(all, getSessionSetLimit());
    const sets = all.map(s => ({ set: s, unlocked: open.includes(s) }));
    if (open.length === 0) return { success: true, data: { sets, questions: [] } };

    // 2. only the unlocked sets are loaded
    const questions: BankQuestion[] = [];
    for (let from = 0; ; from += PAGE) {
      const res = await supabase
        .from('question_bank')
        .select('id, set_no, source_no, category, question, option1, option2, option3, option4, option5, option6, total_option, correct_ans, explanation')
        .eq('exam_id', examId)
        .in('set_no', open)
        .order('set_no', { ascending: true })
        .order('source_no', { ascending: true })
        .range(from, from + PAGE - 1);
      if (res.error) return { success: false, error: res.error.message };
      const rows = (res.data || []) as Row[];
      questions.push(...rows.map(toQuestion));
      if (rows.length < PAGE) break;
    }
    return { success: true, data: { sets, questions } };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}
